import axios, { AxiosResponse } from 'axios';
import { getMaintenanceMode } from 'utils/maintenanceState';

export const globalAxiosSetup = () => {
  axios.defaults.baseURL = process.env.REACT_APP_SERVER_URL_V1;
  axios.defaults.headers.get.Accept = 'application/json';
  axios.defaults.headers.post.Accept = 'application/json';

  axios.interceptors.request.use(
    (config) => {
      if (getMaintenanceMode()) {
        return Promise.reject({ message: 'Service is under maintenance', status: 503 });
      }
      return config;
    },
    (error: unknown) => Promise.reject(error),
  );
};

interface ResolverParams {
  url?: string;
  method?: string;
  params?: unknown;
  data?: unknown;
  requireAuthentication?: boolean;
  headers?: Record<string, string>;
}

const resolver = async ({
  url = '',
  method = '',
  params = undefined,
  data = undefined,
  requireAuthentication = true,
  headers = {},
}: ResolverParams) => {
  const defaultBase = process.env.REACT_APP_SERVER_URL_V1;

  // Add base if url doesn't start with "http://" or "https://"
  const urlWithBase = /^https?:\/\//.test(url) ? url : `${defaultBase}${url}`;

  const mandatoryHeaders = {
    'bludot-app-name': 'business-rewards',
    'Content-Type': 'application/json',
    Authorization: requireAuthentication
      ? `Bearer ${JSON.parse(localStorage.getItem('token') || '')}`
      : '',
  };
  const requestHeaders = { ...mandatoryHeaders, ...headers };
  const config = {
    method,
    params,
    headers: requestHeaders,
    data,
    url: `${urlWithBase}`,
    withCredentials: false,
  };

  if (getMaintenanceMode()) {
    return { error: { message: 'Service is under maintenance', status: 503 } };
  }

  try {
    const response: AxiosResponse = await axios.request(config);
    if (response.status >= 200 && response.status <= 209) {
      return { data: response.data };
    }

    return { error: response.data };
  } catch (err: unknown) {
    const axiosErr = err as {
      response?: { status?: number; config?: { url?: string }; data?: unknown };
    };
    const status = axiosErr?.response?.status;
    const requestUrl = axiosErr?.response?.config?.url;
    return {
      error: {
        ...((axiosErr?.response?.data as Record<string, unknown>) || {}),
        status,
        url: requestUrl,
      },
    };
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IHTTPResponse<T = any> {
  data?: T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
}

export const http = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: async <T = any>(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any = undefined,
    requireAuthentication: boolean = true,
  ): Promise<IHTTPResponse<T>> => resolver({ url, method: 'GET', params, requireAuthentication }),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: async <T = any>(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    requireAuthentication: boolean = true,
    headers: Record<string, string> = {},
  ): Promise<IHTTPResponse<T>> =>
    resolver({ url, method: 'POST', data, requireAuthentication, headers }),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch: async <T = any>(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    requireAuthentication: boolean = true,
    headers: Record<string, string> = {},
  ): Promise<IHTTPResponse<T>> =>
    resolver({ url, method: 'PATCH', data, requireAuthentication, headers }),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put: async <T = any>(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    requireAuthentication: boolean = true,
  ): Promise<IHTTPResponse<T>> => resolver({ url, method: 'PUT', data, requireAuthentication }),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete: async <T = any>(
    url: string,
    requireAuthentication: boolean = true,
  ): Promise<IHTTPResponse<T>> => resolver({ url, method: 'DELETE', requireAuthentication }),
};

export const API_CALL_METHODS = {
  GET: 'get',
  POST: 'post',
  PATCH: 'patch',
};
